import React, { useState, useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TableFooter,
  TablePagination,
  IconButton,
  TextField,
  Button,
  Typography,
  Box,
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";

// Pagination Actions Component
const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const handleFirstPageButtonClick = () => onPageChange(0);
  const handleBackButtonClick = () => onPageChange(page - 1);
  const handleNextButtonClick = () => onPageChange(page + 1);
  const handleLastPageButtonClick = () =>
    onPageChange(Math.max(0, Math.ceil(count / rowsPerPage) - 1));

  return (
    <>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0}>
        <FirstPage />
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0}>
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        <LastPage />
      </IconButton>
    </>
  );
};

const ReactMuiTable = ({
  columns,
  data,
  pageIndex,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}) => {
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState(data);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable(
      {
        columns,
        data: filteredData,
        manualPagination: true,
        manualSortBy: true,
      },
      useSortBy
    );

  const handleSearch = (e) => {
    if (searchText.trim() === "" || searchText.length < 3) {
      setFilteredData(data);
    } else {
      if (e.target.value.length > 3) {
        const lower = searchText.toLowerCase();
        const result = data.filter((row) =>
          Object.values(row).some((val) =>
            String(val).toLowerCase().includes(lower)
          )
        );
        setFilteredData(result);
      }
    }
  };

  return (
    <>
      <Box display="flex" gap={2} justifyContent="flex-end" padding={2}>
        <TextField
          label="Search"
          variant="outlined"
          style={{ width: "300px" }}
          size="small"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);

            handleSearch(e);
          }}
        />
        {/* <Button
          variant="outlined"
          color="grey"
          className="clr_btn clr_btn_1"
          onClick={(e) => {
            setSearchText("");
            setFilteredData(data);
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={handleSearch}
          className="srch_blue srchbllue_1"
        >
          Search
        </Button> */}
      </Box>

      <TableContainer component={Paper}>
        <Table {...getTableProps()} className="table-striped table-responsive">
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <TableCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{ fontWeight: "bold", cursor: "pointer" }}
                  >
                    {column.render("Header")}
                    {column.isSorted
                      ? column.isSortedDesc
                        ? " 🔽"
                        : " 🔼"
                      : ""}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography textAlign="center">No records found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      const cellValue = cell.value;
                      const isNumeric = typeof cellValue === "number";
                      return (
                        <TableCell
                          {...cell.getCellProps()}
                          style={{ textAlign: isNumeric ? "right" : "left" }}
                        >
                          {cell.render("Cell")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={totalCount}
                page={pageIndex}
                rowsPerPage={pageSize}
                onPageChange={(_, newPage) => onPageChange(newPage)}
                onRowsPerPageChange={(e) =>
                  onPageSizeChange(Number(e.target.value))
                }
                rowsPerPageOptions={[5, 10, 20, 30, 50]}
                ActionsComponent={(subProps) => (
                  <TablePaginationActions
                    {...subProps}
                    count={totalCount}
                    page={pageIndex}
                    rowsPerPage={pageSize}
                    onPageChange={onPageChange}
                  />
                )}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </>
  );
};

export default ReactMuiTable;
